import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import { getPublishedPosts } from '../services/blogService.ts';
import type { BlogListItem } from '../types/blog.ts';

export default function BlogScreen() {
  const [posts, setPosts] = useState<BlogListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[BlogScreen] Component mounted');
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      console.log('[BlogScreen] Loading published posts...');
      setLoading(true);
      setError(null);
      const data = await getPublishedPosts();
      setPosts(data);
      console.log('[BlogScreen] Loaded', data.length, 'published posts');
    } catch (err) {
      console.error('[BlogScreen] Error loading posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatReadTime = (content: string) => {
    // Estimate reading time based on word count (average 200 words per minute)
    const wordCount = content.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / 200);
    return `${readTime} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Loading blog posts...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="container mx-auto px-6 py-16">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
                SafeVibe Security Blog
              </h1>
              <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
                Latest insights on web application security, vulnerability detection, 
                and best practices for keeping your code safe.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 py-12">
          {error ? (
            <div className="text-center py-12">
              <div className="bg-red-50 border border-red-200 rounded-md p-4 max-w-md mx-auto">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">No Posts Yet</h2>
                <p className="text-gray-600 mb-6">
                  We're working on creating amazing content about web security. 
                  Check back soon for the latest insights and tutorials!
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  Explore SafeVibe
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {/* New AI Explains Code Risks Article */}
              <a
                href="https://safevibe.dev/blog/how-ai-explains-code-risks"
                className="block bg-white rounded-xl shadow hover:shadow-lg transition p-6 border border-blue-100 hover:border-blue-300"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h2 className="text-2xl font-bold text-blue-700 mb-2">How our AI explains real code risks</h2>
                <p className="text-gray-600 mb-4">See how SafeVibe uses AI to break down security findings and help you fix them, even if you're new to code security.</p>
                <span className="inline-block text-blue-600 font-semibold">Read article →</span>
              </a>
              {posts.map((post) => (
                <article 
                  key={post.slug}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200"
                >
                  {post.cover_url && (
                    <div className="aspect-w-16 aspect-h-9">
                      <img
                        src={post.cover_url}
                        alt={post.title}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <span>{formatDate(post.published_at)}</span>
                      <span className="mx-2">•</span>
                      <span>{post.author}</span>
                    </div>
                    
                    <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                      <Link 
                        to={`/blog/${post.slug}`}
                        className="hover:text-blue-600 transition-colors"
                      >
                        {post.title}
                      </Link>
                    </h2>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {post.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag}
                          </span>
                        ))}
                        {post.tags.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{post.tags.length - 2} more
                          </span>
                        )}
                      </div>
                      
                      <Link
                        to={`/blog/${post.slug}`}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        Read more →
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-blue-600">
          <div className="container mx-auto px-6 py-12">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Scan Your App?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Put our security insights to work. Scan your web application 
                for vulnerabilities and security issues in under 30 seconds.
              </p>
              <Link
                to="/"
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-colors"
              >
                Start Free Scan
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
} 